import { useState, useCallback, useEffect, useMemo } from "react";
import { observer } from "mobx-react-lite";
import { Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { OrgTreeSelect } from "@/components/common/OrgTreeSelect";
import { filterOrgTreeByIds } from "@/features/data-entry/utils/orgTree.helpers";
import { useTran } from "@/hooks";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/features/auth/hooks";
import {
  useTargetSetupStore,
  useTargetSetupUIStore,
} from "../hooks/useTargetSetupStore";
import {
  loadOrgList,
  updateDisclosureTargetFrequency,
} from "../operations/target-setup.ops";
import { buildAssignedOrgTree } from "../domains/assignmentScope";
import { flattenVisibleTree } from "../domains/flattenVisibleTree";
import { filterTree, countOrgs } from "../domains/orgTree.utils";
import {
  buildDisclosureYearKey,
  resolveDisclosureId,
  isMonthlyMode,
} from "../domains/targetSetup";
import VirtualOrgTree from "./VirtualOrgTree";
import OrgListSkeleton from "./skeleton/OrgListSkeleton";

const PeriodDisplayToggle = ({ value, onChange, disabled = false }) => {
  const { t } = useTran();
  const options = [
    { value: "yearly", label: t("Yearly") },
    { value: "monthly", label: t("Monthly") },
  ];

  return (
    <div className="flex h-9 shrink-0 items-center rounded-md border border-primary/30 bg-background p-0.5">
      {options.map((option) => {
        const isActive = value === option.value;
        return (
          <button
            type="button"
            key={option.value}
            disabled={disabled}
            onClick={() => onChange?.(option.value)}
            className={cn(
              "flex h-7 min-w-20 items-center justify-center rounded px-3 text-xs font-semibold transition-colors",
              isActive
                ? "bg-primary text-primary-foreground shadow-sm"
                : "text-primary hover:bg-primary/5",
              disabled && "cursor-not-allowed opacity-50",
            )}
            aria-pressed={isActive}
          >
            {option.label}
          </button>
        );
      })}
    </div>
  );
};

const ExpandedDisclosurePanel = observer(
  ({ disclosure, year, monthOptions }) => {
    const { t } = useTran();
    const { user } = useAuthStore();
    const { filteredOrgTree, orgTreeLoaded, targetFrequency } =
      useTargetSetupStore();
    const {
      loadingTargets,
      loadingOrgTree,
      getSelectedOrgs,
      setSelectedOrgs,
      getDisclosureTargetFrequency,
      savePendingRowsForDisclosure,
    } = useTargetSetupUIStore();

    const [orgSearch, setOrgSearch] = useState("");
    const [savingFrequency, setSavingFrequency] = useState(false);
    const [collapsedNodes, setCollapsedNodes] = useState(() => new Set());
    const disclosureKey = buildDisclosureYearKey(
      resolveDisclosureId(disclosure) || disclosure?.code || disclosure?.title,
      year,
    );
    const selectedOrgs = getSelectedOrgs(disclosureKey);

    useEffect(() => {
      if (!orgTreeLoaded) {
        void loadOrgList();
      }
    }, [orgTreeLoaded]);

    const assignedOrgTree = useMemo(() => {
      return buildAssignedOrgTree(disclosure, filteredOrgTree);
    }, [disclosure, filteredOrgTree]);

    const orgFilteredTree = useMemo(() => {
      if (!selectedOrgs.length) return assignedOrgTree;
      return filterOrgTreeByIds(assignedOrgTree, new Set(selectedOrgs));
    }, [assignedOrgTree, selectedOrgs]);

    const visibleTree = useMemo(
      () => filterTree(orgFilteredTree, orgSearch),
      [orgFilteredTree, orgSearch],
    );

    const flatRows = useMemo(
      () => flattenVisibleTree(visibleTree, collapsedNodes),
      [visibleTree, collapsedNodes],
    );

    const totalCount = useMemo(
      () => countOrgs(orgFilteredTree),
      [orgFilteredTree],
    );
    const visibleCount = useMemo(() => countOrgs(visibleTree), [visibleTree]);

    const handleToggleCollapse = useCallback((key) => {
      setCollapsedNodes((prev) => {
        const next = new Set(prev);
        if (next.has(key)) next.delete(key);
        else next.add(key);
        return next;
      });
    }, []);

    const hasUnit = Boolean(disclosure?.defaultUnit);
    const showPeriodToggle = user?.enableMonthlyYearlySwitch === true;
    const selectedDisclosureTargetFrequency = getDisclosureTargetFrequency(
      disclosureKey,
      targetFrequency,
    );
    const disclosureTargetFrequency = showPeriodToggle
      ? selectedDisclosureTargetFrequency
      : targetFrequency;
    const isMonthly = isMonthlyMode(disclosureTargetFrequency);

    if (loadingTargets || loadingOrgTree || !orgTreeLoaded) {
      return (
        <div className="px-4 py-6">
          <OrgListSkeleton />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-3 px-4 py-4">
        {/* Header */}
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            {t("Organizations")}
          </span>
          <span className="text-xs text-muted-foreground">
            {visibleCount}/{totalCount}
          </span>
        </div>

        {/* Org filter + Search */}
        <div className="flex items-center gap-2">
          <OrgTreeSelect
            tree={assignedOrgTree}
            value={selectedOrgs}
            onChange={(next) => setSelectedOrgs(disclosureKey, next)}
            multiSelect
            placeholder={t("All Organizations")}
            helperText=""
            showAllOption={false}
            className="w-[240px] shrink-0"
          />
          <div className="relative flex-1">
            <Search
              size={16}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              placeholder={t("Filter organizations...")}
              value={orgSearch}
              onChange={(e) => setOrgSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          {showPeriodToggle ? (
            <PeriodDisplayToggle
              value={disclosureTargetFrequency}
              disabled={savingFrequency}
              onChange={async (frequency) => {
                if (
                  savingFrequency ||
                  frequency === disclosureTargetFrequency
                ) {
                  return;
                }
                setSavingFrequency(true);
                const pendingRowsSaved =
                  await savePendingRowsForDisclosure(disclosureKey);
                if (!pendingRowsSaved) {
                  setSavingFrequency(false);
                  return;
                }
                await updateDisclosureTargetFrequency({
                  disclosureId: resolveDisclosureId(disclosure),
                  year,
                  targetFrequency: frequency,
                });
                setSavingFrequency(false);
              }}
            />
          ) : null}
        </div>

        {/* Column headers */}
        <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-xs font-medium text-muted-foreground">
          <span className="flex-1">{t("Organization")}</span>
          {isMonthly ? (
            <span className="flex items-center gap-1.5">
              {t("Monthly Targets")}
              {hasUnit ? (
                <span className="rounded-full bg-muted px-2 py-0.5 text-[11px] font-semibold text-foreground">
                  {disclosure.defaultUnit}
                </span>
              ) : null}
            </span>
          ) : (
            <>
              <span className="w-40 text-center">{t("Target Value")}</span>
              {hasUnit ? (
                <span className="w-40 text-center">{t("Unit")}</span>
              ) : null}
            </>
          )}
          <span className="w-20" />
        </div>

        {/* Virtualized org tree */}
        {flatRows.length > 0 ? (
          <VirtualOrgTree
            flatRows={flatRows}
            disclosure={disclosure}
            year={year}
            monthOptions={monthOptions}
            targetFrequency={disclosureTargetFrequency}
            collapsedNodes={collapsedNodes}
            onToggleCollapse={handleToggleCollapse}
          />
        ) : (
          <p className="py-6 text-center text-sm text-muted-foreground">
            {t("No organizations found")}
          </p>
        )}
      </div>
    );
  },
);

export default ExpandedDisclosurePanel;
